import React, { useState, useEffect, useRef } from 'react';
import { Link2, Copy, Check, AlertTriangle, RefreshCw, User, MessageCircle, Clock, Send, Smartphone } from 'lucide-react';
import { authHeaders } from '../../../core/api/authHeaders';

const PROXY_BASE = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');

// Watch cadence: the customer usually pays while the agent is still on the
// call, so the first 2 minutes are checked every 10 seconds; after that every
// minute. The panel gives up on its own after 30 minutes — "Check now" still
// asks the gateway on demand, and the hourly reconciler catches late payments.
const FAST_MS = 10_000, SLOW_MS = 60_000, FAST_WINDOW_MS = 2 * 60_000, GIVE_UP_MS = 30 * 60_000;

/**
 * Collect the just-created order's payment over a shareable link.
 * The SERVER prices the link from the order's own total — this panel only
 * requests it, shares it, and watches the status live.
 */
const PURPOSE_TEXT = {
    full: 'Payment',
    cod_advance: 'COD advance',
    cod_balance: 'Converted to prepaid',
};

/**
 * @param purpose      'full' | 'cod_advance' | 'cod_balance' — WHAT to collect; the server
 *                     prices it from the order, `total` is only the button label.
 * @param initialLink  an existing link for the same purpose: shown and watched instead of
 *                     offering to create a new one.
 */
const PaymentLinkPanel = ({ orderKey, orderId, total, mobile, agentName, purpose = 'full', initialLink = null, confirmOutside = false }) => {
    const [link, setLink] = useState(() => (initialLink?.url ? {
        url: initialLink.url, status: initialLink.status || 'ACTIVE', amount: initialLink.amount,
        createdAt: initialLink.createdAt || Date.now(), paid: String(initialLink.status || '').toUpperCase() === 'PAID', amountPaid: 0,
    } : null)); // { url, status, amount, createdAt, paid, amountPaid }
    const [doublePayment, setDoublePayment] = useState(null);
    const [busy, setBusy] = useState(false);
    const [checking, setChecking] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [smsState, setSmsState] = useState('idle');   // idle | sending | sent
    const [smsLabel, setSmsLabel] = useState('SMS sent');
    const [stoppedWatching, setStoppedWatching] = useState(false);
    const timerRef = useRef(null);
    const aliveRef = useRef(true);
    const linkRef = useRef(null);
    linkRef.current = link;

    useEffect(() => () => { aliveRef.current = false; clearTimeout(timerRef.current); }, []);
    // An existing link: start watching it straight away.
    useEffect(() => { if (initialLink?.url) setTimeout(() => checkStatusRef.current(false), 300); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const api = async (path, opts = {}) => {
        const r = await fetch(`${PROXY_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', ...(await authHeaders()) } });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
        return j;
    };

    // Plain functions + refs (not useCallback): the two call each other and read
    // only refs/stable setters, so stale closures cannot bite.
    const checkStatusRef = useRef(() => {});
    const scheduleNext = () => {
        clearTimeout(timerRef.current);
        const l = linkRef.current;
        if (!aliveRef.current || !l || l.paid || ['PAID', 'EXPIRED', 'CANCELLED'].includes(l.status)) return;
        const age = Date.now() - (l.createdAt || Date.now());
        if (age > GIVE_UP_MS) { setStoppedWatching(true); return; }
        timerRef.current = setTimeout(() => checkStatusRef.current(false), age < FAST_WINDOW_MS ? FAST_MS : SLOW_MS);
    };
    const checkStatus = async (manual = false) => {
        if (!linkRef.current || !aliveRef.current) return;
        if (manual) { setChecking(true); setError(''); }
        try {
            const j = await api(`/api/payment-links/status?key=${encodeURIComponent(orderKey)}`);
            if (!aliveRef.current) return;
            linkRef.current = linkRef.current ? { ...linkRef.current, status: j.linkStatus, paid: j.paid, amountPaid: j.amountPaid } : linkRef.current;
            setLink(linkRef.current);
            if (j.doublePayment) setDoublePayment(j.doublePayment);
            if (manual) setStoppedWatching(false);
        } catch (e) {
            // A failed CHECK is not a verdict — keep watching quietly, but surface
            // the problem when the agent asked by hand.
            if (manual && aliveRef.current) setError(e.message);
        } finally {
            if (aliveRef.current) { setChecking(false); scheduleNext(); }
        }
    };
    checkStatusRef.current = checkStatus;

    const createLink = async () => {
        setBusy(true); setError('');
        try {
            const j = await api('/api/payment-links/create', { method: 'POST', body: JSON.stringify({ key: orderKey, agentName, purpose, ...(confirmOutside ? { confirmOutside: true } : {}) }) });
            if (!aliveRef.current) return;
            linkRef.current = { url: j.url, status: j.status, amount: j.amount, createdAt: j.createdAt || Date.now(), paid: false, amountPaid: 0 };
            setLink(linkRef.current);
            // Fetch immediately, then fall into the 10s → 1min cadence.
            setTimeout(() => checkStatusRef.current(false), 300);
        } catch (e) {
            if (aliveRef.current) setError(e.message);
        } finally {
            if (aliveRef.current) setBusy(false);
        }
    };

    const copy = async () => {
        try { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
    };

    // Text the link from the ERP over the MSG91/DLT gateway. Nothing touches a
    // phone, so an agent can send it without leaving the call.
    const sendSms = async (channel = 'gateway') => {
        setSmsState('sending'); setError('');
        try {
            const j = await api('/api/payment-links/send-sms', { method: 'POST', body: JSON.stringify({ key: orderKey, mobile: m10, channel }) });
            if (j.status === 'sent' || j.status === 'queued') {
                setSmsState('sent');
                // From the phone it is only QUEUED here — the handset confirms
                // a moment later, so don't claim it was delivered.
                setSmsLabel(j.status === 'queued' ? 'Sent to your phone' : 'SMS sent');
                setTimeout(() => setSmsState('idle'), 8000);
            } else if (j.status === 'skipped') {
                setSmsState('idle');
                setError(j.error || 'Already sent in the last 5 minutes.');
            } else {
                setSmsState('idle');
                setError(j.error || 'Could not send the SMS.');
            }
        } catch (e) {
            setSmsState('idle');
            setError(e.message || 'Could not send the SMS.');
        }
    };
    const m10 = String(mobile || '').replace(/\D/g, '').slice(-10);
    const waText = purpose === 'cod_advance'
        ? `Hi! Please pay the advance of ₹${link?.amount} for your Daksha Organics order ${orderId} using this secure link — the rest is paid on delivery:\n${link?.url}`
        : `Hi! Please complete your Daksha Organics payment of ₹${link?.amount} for order ${orderId} using this secure link:\n${link?.url}`;
    const waHref = link ? `https://wa.me/91${m10}?text=${encodeURIComponent(waText)}` : '#';

    if (!link) {
        return (
            <div className="mt-2">
                <button onClick={createLink} disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50">
                    <Link2 size={14} /> {busy ? 'Creating payment link…' : (purpose === 'cod_balance' ? `Convert to prepaid & send ₹${total} link` : `Collect ${purpose === 'full' ? '' : `${PURPOSE_TEXT[purpose]} `}₹${total} via Payment Link`)}
                </button>
                {error && <div className="mt-1 text-[11px] font-semibold text-rose-600 flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
            </div>
        );
    }

    const paid = link.paid || link.status === 'PAID';
    const dead = ['EXPIRED', 'CANCELLED'].includes(link.status);
    return (
        <div className={`mt-2 rounded-lg border p-3 ${paid ? 'border-emerald-300 bg-emerald-50' : dead ? 'border-slate-200 bg-slate-50' : 'border-amber-300 bg-amber-50/60'}`}>
            <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="font-black uppercase tracking-wide text-slate-700 flex items-center gap-1"><Link2 size={13} /> {PURPOSE_TEXT[purpose] || 'Payment'} via link</span>
                <span className="inline-flex items-center gap-1 text-slate-500"><User size={12} /> {agentName}</span>
                <span className="font-bold text-slate-700">₹{link.amount}</span>
                {paid ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white font-bold"><Check size={12} /> PAYMENT RECEIVED{link.amountPaid ? ` · ₹${link.amountPaid}` : ''}</span>
                ) : dead ? (
                    <span className="px-2 py-0.5 rounded-full bg-slate-500 text-white font-bold">{link.status}</span>
                ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white font-bold">
                        <Clock size={12} className="animate-pulse" /> WAITING FOR PAYMENT
                    </span>
                )}
            </div>
            {!paid && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <input readOnly value={link.url} onFocus={e => e.target.select()}
                        className="flex-1 min-w-[220px] text-[11px] font-mono px-2 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600" />
                    <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 hover:border-purple-400 text-xs font-bold text-slate-700">
                        {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}
                    </button>
                    {m10.length === 10 && (
                        <a href={waHref} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold">
                            <MessageCircle size={13} /> WhatsApp
                        </a>
                    )}
                    {/* Straight from the server over the DLT gateway — the agent
                        can stay on the call and never touches a phone. */}
                    {m10.length === 10 && (smsState === 'sent' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-bold">
                            <Check size={13} /> {smsLabel}
                        </span>
                    ) : (
                        <>
                            <button onClick={() => sendSms('gateway')} disabled={smsState === 'sending'}
                                title={`Text this link to ${m10} from the company sender id`}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold disabled:opacity-60">
                                <Send size={13} /> {smsState === 'sending' ? 'Sending…' : 'SMS'}
                            </button>
                            <button onClick={() => sendSms('phone')} disabled={smsState === 'sending'}
                                title={`Send from YOUR phone to ${m10} — you stay on the call, no need to touch the handset`}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 text-xs font-bold disabled:opacity-60">
                                <Smartphone size={13} /> From my phone
                            </button>
                        </>
                    ))}
                    <button onClick={() => checkStatus(true)} disabled={checking}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-white border border-slate-200 hover:border-purple-400 text-xs font-bold text-slate-700 disabled:opacity-50">
                        <RefreshCw size={13} className={checking ? 'animate-spin' : ''} /> Check now
                    </button>
                </div>
            )}
            {!paid && !dead && (
                <div className="mt-1.5 text-[10px] text-slate-500">
                    {stoppedWatching
                        ? 'Auto-check stopped after 30 minutes — use “Check now”, the hourly verify still catches a late payment.'
                        : (Date.now() - (link.createdAt || 0) < FAST_WINDOW_MS
                            ? 'Watching the gateway every 10 seconds…'
                            : 'Watching the gateway every minute…')}
                </div>
            )}
            {doublePayment && (
                <div className="mt-2 rounded-md bg-rose-600 text-white text-[11px] font-bold px-2.5 py-1.5">
                    DOUBLE PAYMENT — the customer also paid at the original checkout. Refund ₹{doublePayment.refundDue}
                    {doublePayment.link?.reference ? ` (link UTR ${doublePayment.link.reference})` : ''}.
                </div>
            )}
            {error && <div className="mt-1 text-[11px] font-semibold text-rose-600 flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
        </div>
    );
};

export default PaymentLinkPanel;
